import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { formatDate } from '../../utils/format';
import { useToast } from '../../contexts/ToastContext';
import EventForm from '../../components/EventForm';

const statusLabels = {
  draft: 'Brouillon',
  published: 'Publié',
  cancelled: 'Annulé',
  completed: 'Terminé',
};

const eventStatusColors = {
  draft: 'th-border th-text-muted',
  published: 'border-accent text-accent bg-accent/10',
  cancelled: 'border-status-rejected text-status-rejected bg-status-rejected/10',
  completed: 'th-border th-text-secondary',
};

export default function PromoterEventsSection({ events, onEventsChange, initialEditingEventId }) {
  const { addToast } = useToast();
  const [showEventForm, setShowEventForm] = useState(false);
  const [editingEventId, setEditingEventId] = useState(null);

  // Deep-link ?editEvent=... : la page pose l'id en state et la section ouvre l'édition
  useEffect(() => {
    if (initialEditingEventId) {
      setEditingEventId(initialEditingEventId);
    }
  }, [initialEditingEventId]);

  async function handleEventCreated() {
    try {
      const res = await api.get('/events/mine');
      onEventsChange(res.data.events || []);
      setShowEventForm(false);
      setEditingEventId(null);
      addToast('Événement créé');
    } catch (err) {
      console.error('Failed to refetch events:', err);
    }
  }

  async function handlePublishEvent(eventId) {
    try {
      await api.patch(`/events/${eventId}/status`, { status: 'published' });
      onEventsChange((prev) =>
        prev.map((e) => (e.id === eventId ? { ...e, status: 'published' } : e))
      );
      addToast('Événement publié');
    } catch (err) {
      addToast(err.message, 'error');
    }
  }

  async function handleCancelEvent(eventId) {
    if (!confirm('Annuler cet événement ?')) return;
    try {
      await api.patch(`/events/${eventId}/status`, { status: 'cancelled' });
      onEventsChange((prev) =>
        prev.map((e) => (e.id === eventId ? { ...e, status: 'cancelled' } : e))
      );
      addToast('Événement annulé');
    } catch (err) {
      addToast(err.message, 'error');
    }
  }

  return (
    <section className="mb-10">
      <div className="flex items-center justify-between mb-4">
        <h2 className="th-section-title">Mes Événements</h2>
        <button
          onClick={() => setShowEventForm(!showEventForm)}
          className="th-btn-accent"
        >
          Créer un événement
        </button>
      </div>

      {showEventForm && (
        <div className="mb-6 border th-border p-6 th-bg-surface">
          <EventForm onSuccess={handleEventCreated} onCancel={() => setShowEventForm(false)} />
        </div>
      )}

      {events.length === 0 ? (
        <div className="border th-border p-6 text-center th-text-secondary text-sm th-bg-surface">
          Aucun événement créé. Commencez par en créer un.
        </div>
      ) : (
        <div className="space-y-px dark:bg-dark-border bg-light-border">
          {events.map((event) => (
            <div key={event.id}>
              {editingEventId === event.id ? (
                <div className="th-bg p-5 space-y-4">
                  <EventForm
                    initialData={event}
                    mode="edit"
                    onSuccess={handleEventCreated}
                    onCancel={() => setEditingEventId(null)}
                  />
                </div>
              ) : (
                <div className="th-bg p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/events/${event.slug}`}
                      className="font-mono font-medium th-text text-sm hover:text-accent transition-colors duration-150"
                    >
                      {event.title}
                    </Link>
                    <p className="text-xs th-text-muted font-mono mt-0.5">
                      {formatDate(event.eventDate)} · {event.venueName || event.location?.city || 'Pas de lieu'}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className={`th-badge ${eventStatusColors[event.status]}`}>
                      {statusLabels[event.status]}
                    </span>
                    <button
                      onClick={() => setEditingEventId(event.id)}
                      className="th-btn text-xs"
                    >
                      Modifier
                    </button>
                    {event.status === 'draft' && (
                      <button
                        onClick={() => handlePublishEvent(event.id)}
                        className="th-btn-accent active:opacity-70 transition-opacity duration-100"
                      >
                        Publier
                      </button>
                    )}
                    {event.status === 'published' && (
                      <button
                        onClick={() => handleCancelEvent(event.id)}
                        className="border border-status-rejected/30 text-status-rejected text-xs font-mono uppercase tracking-widest px-3 py-1.5 hover:bg-status-rejected/10 transition-colors duration-150 active:opacity-70"
                      >
                        Annuler
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
